import axios, { AxiosInstance } from 'axios';
import { ApiRequestError } from './apiErrors';

/**
 * Thrown when a Whop endpoint reports the app's API key lacks a required
 * permission scope (creators who installed before promo permissions were
 * added). Pickers / dialogs check `instanceof WhopPermissionRequiredError`
 * and render an upgrade card linking to the authorized-apps settings page.
 */
export class WhopPermissionRequiredError extends Error {
  readonly missingPermission: string | null;

  constructor(message: string, missingPermission: string | null) {
    super(message);
    this.name = 'WhopPermissionRequiredError';
    this.missingPermission = missingPermission;
  }
}
/** Server error codes for plan limits (402); the UI opens an upgrade prompt. */
export type PlanErrorCode = 'PLAN_LIMIT_FREE_TEST_USED';

/** A 402: the free test is used, and this would start another without Pro. */
export class PlanLimitError extends Error {
  readonly code: PlanErrorCode;

  constructor(message: string, code: PlanErrorCode) {
    super(message);
    this.name = 'PlanLimitError';
    this.code = code;
  }
}

import type {
  Experiment,
  ExperimentMetrics,
  VariantTimeSeriesResponse,
  CreateExperimentRequest,
  ApiResponse,
  PromotionContext,
  Product,
  ProductPlan,
  EligiblePromo,
  PublicOffer,
  CreateUnlockTestRequest,
  ExperienceView,
  ExperienceAttachResult,
  Placement,
  CreatePlacementRequest,
  PlacementBreakdownResponse,
  PlanLimits,
  VisitActivity,
  TestOverview,
  PurchaseInfo,
  SellerInfo,
  HeldInWhopSpot,
  RemovePlacementResponse,
} from '../types';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: '/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Important for cookies/auth headers
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const data = error.response?.data;
        // Never axios's own "Request failed with status code 404".
        const message =
          data?.message ||
          (error.response
            ? 'Something went wrong on our side. Try again.'
            : "We couldn't reach Paywall Lab. Check your connection and try again.");

        // Preserve structured permission errors so callers (the promo picker)
        // can render an upgrade prompt rather than a generic error message.
        if (data?.error === 'WHOP_PERMISSION_REQUIRED') {
          throw new WhopPermissionRequiredError(
            message,
            data.missingPermission ?? null
          );
        }

        if (data?.code === 'PLAN_LIMIT_FREE_TEST_USED') {
          throw new PlanLimitError(message, data.code);
        }

        throw new ApiRequestError(message, error.response?.status, data?.blockingTest);
      }
    );
  }

  // Experiments
  async createExperiment(data: CreateExperimentRequest): Promise<Experiment> {
    const response = await this.client.post<ApiResponse<Experiment>>(
      '/experiments',
      data
    );
    return response.data.data;
  }

  async getExperiment(id: string): Promise<Experiment> {
    const response = await this.client.get<ApiResponse<Experiment>>(
      `/experiments/${id}`
    );
    return response.data.data;
  }

  async listExperiments(
    companyId: string,
    status?: string,
    includeArchived?: boolean,
    includeStats?: boolean
  ): Promise<Experiment[]> {
    const response = await this.client.get<ApiResponse<Experiment[]>>(
      '/experiments',
      {
        params: { companyId, status, includeArchived, includeStats },
      }
    );
    return response.data.data;
  }

  async startExperiment(id: string): Promise<Experiment> {
    const response = await this.client.post<ApiResponse<Experiment>>(
      `/experiments/${id}/start`
    );
    return response.data.data;
  }

  async stopExperiment(id: string): Promise<Experiment> {
    const response = await this.client.post<ApiResponse<Experiment>>(
      `/experiments/${id}/stop`
    );
    return response.data.data;
  }

  async resumeExperiment(id: string): Promise<Experiment> {
    const response = await this.client.post<ApiResponse<Experiment>>(
      `/experiments/${id}/resume`
    );
    return response.data.data;
  }

  async getPromotionContext(id: string): Promise<PromotionContext> {
    const response = await this.client.get(
      `/experiments/${id}/promotion-context`
    );
    return response.data.data;
  }

  async promoteWinner(
    id: string,
    winnerVariantId: string,
    hidePlanIds?: string[],
    archivePromoIds?: string[]
  ): Promise<Experiment> {
    const response = await this.client.post<ApiResponse<Experiment>>(
      `/experiments/${id}/promote-winner`,
      { winnerVariantId, hidePlanIds, archivePromoIds }
    );
    return response.data.data;
  }

  /** Delete a draft nobody has visited; anything else is archived. */
  async discardDraft(id: string): Promise<{ deleted: boolean }> {
    const response = await this.client.post<ApiResponse<{ deleted: boolean }>>(
      `/experiments/${id}/discard`
    );
    return response.data.data;
  }

  async archiveExperiment(id: string): Promise<{
    experiment: Experiment;
    wasRunning: boolean;
  }> {
    const response = await this.client.post<
      ApiResponse<Experiment> & { wasRunning: boolean }
    >(`/experiments/${id}/archive`);
    return {
      experiment: response.data.data,
      wasRunning: response.data.wasRunning,
    };
  }

  async listEligiblePromos(
    companyId: string,
    productId: string
  ): Promise<EligiblePromo[]> {
    const response = await this.client.get<ApiResponse<EligiblePromo[]>>(
      '/promo-codes/eligible',
      { params: { companyId, productId } }
    );
    return response.data.data;
  }

  async unarchiveExperiment(id: string): Promise<Experiment> {
    const response = await this.client.post<ApiResponse<Experiment>>(
      `/experiments/${id}/unarchive`
    );
    return response.data.data;
  }

  // Analytics
  async getExperimentMetrics(id: string, companyId: string): Promise<ExperimentMetrics> {
    const response = await this.client.get<ApiResponse<ExperimentMetrics>>(
      `/analytics/experiments/${id}`,
      { params: { companyId } }
    );
    return response.data.data;
  }

  async getVariantTimeSeriesData(
    id: string,
    companyId: string,
    startDate: Date,
    endDate: Date,
    interval: 'hour' | 'day' = 'day'
  ): Promise<VariantTimeSeriesResponse> {
    const response = await this.client.get<ApiResponse<VariantTimeSeriesResponse>>(
      `/analytics/experiments/${id}/timeseries/variants`,
      {
        params: {
          companyId,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          interval,
          // Bucket by the viewer's calendar day, not UTC's
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }
    );
    return response.data.data;
  }

  // Per-placement, per-variant breakdown.
  async getPlacementBreakdown(id: string): Promise<PlacementBreakdownResponse> {
    const response = await this.client.get<ApiResponse<PlacementBreakdownResponse>>(
      `/analytics/experiments/${id}/placements`
    );
    return response.data.data;
  }

  // Home: placements, real visitors and metrics for every non-archived test.
  async getOverview(companyId: string): Promise<TestOverview[]> {
    const response = await this.client.get<ApiResponse<TestOverview[]>>('/overview', {
      params: { companyId },
    });
    return response.data.data;
  }

  // Unique visitors so far, excluding the seller's own verified visits.
  async getVisitActivity(id: string): Promise<VisitActivity> {
    const response = await this.client.get<ApiResponse<VisitActivity>>(
      `/analytics/experiments/${id}/activity`
    );
    return response.data.data;
  }

  // Placements
  async getPlacements(experimentId: string): Promise<Placement[]> {
    const response = await this.client.get<ApiResponse<Placement[]>>(
      `/placements/${experimentId}`
    );
    return response.data.data;
  }

  async createPlacement(
    experimentId: string,
    data: CreatePlacementRequest
  ): Promise<Placement> {
    const response = await this.client.post<ApiResponse<Placement>>(
      `/placements/${experimentId}`,
      data
    );
    return response.data.data;
  }

  /** Rename a spot, or change a partner's Whop username. Its link stays. */
  async updatePlacement(
    experimentId: string,
    placementId: string,
    updates: { label?: string; affiliateCode?: string }
  ): Promise<Placement> {
    const response = await this.client.patch<ApiResponse<Placement>>(
      `/placements/${experimentId}/${placementId}`,
      updates
    );
    return response.data.data;
  }

  async removePlacement(experimentId: string, placementId: string): Promise<RemovePlacementResponse> {
    const response = await this.client.delete<ApiResponse<RemovePlacementResponse>>(
      `/placements/${experimentId}/${placementId}`
    );
    return response.data.data;
  }

  /** Does Whop know this username? Null when Whop couldn't say. */
  async checkUsername(companyId: string, username: string): Promise<boolean | null> {
    const response = await this.client.get<ApiResponse<{ exists: boolean | null }>>(
      '/placements/check-username',
      { params: { companyId, username } }
    );
    return response.data.data.exists;
  }

  /** Free products a running test holds. */
  async getHeldInWhopSpots(companyId: string): Promise<HeldInWhopSpot[]> {
    const response = await this.client.get<ApiResponse<HeldInWhopSpot[]>>('/placements/in-whop-spots', {
      params: { companyId },
    });
    return response.data.data;
  }

  /** The real seller name and benefits for the wizard's member preview. */
  async getMemberPreview(
    companyId: string,
    productId: string
  ): Promise<{ sellerName: string | null; benefits: string[] }> {
    const response = await this.client.get<
      ApiResponse<{ sellerName: string | null; benefits: string[] }>
    >('/experiences/preview', { params: { companyId, productId } });
    return response.data.data;
  }

  /**
   * Link an in_whop placement to its Whop experience: finds the app on the
   * free product, or with `attach` adds it (needs experience permissions).
   */
  async linkPlacementExperience(
    experimentId: string,
    placementId: string,
    attach: boolean
  ): Promise<{ placement: Placement; attach: ExperienceAttachResult }> {
    const response = await this.client.post<
      ApiResponse<{ placement: Placement; attach: ExperienceAttachResult }>
    >(`/placements/${experimentId}/${placementId}/experience`, { attach });
    return response.data.data;
  }

  // Router
  async getVariantAssignment(
    experimentId: string,
    signature: string,
    expires: string,
    passthrough: Record<string, string> = {}
  ): Promise<{
    experimentId: string;
    variantId: string;
    variantName: string;
    purchaseUrl: string;
  }> {
    const response = await this.client.get<
      ApiResponse<{
        experimentId: string;
        variantId: string;
        variantName: string;
        purchaseUrl: string;
      }>
    >(`/router/${experimentId}`, {
      params: { ...passthrough, signature, expires },
    });
    return response.data.data;
  }

  async getVariantAssignmentByShareCode(
    companyRoute: string,
    shareCode: string,
    passthrough: Record<string, string> = {}
  ): Promise<{
    experimentId: string;
    variantId: string;
    variantName: string;
    purchaseUrl: string;
    offer?: PublicOffer; // unlock tests: render the hosted offer page instead
  }> {
    const response = await this.client.get<
      ApiResponse<{
        experimentId: string;
        variantId: string;
        variantName: string;
        purchaseUrl: string;
        offer?: PublicOffer;
      }>
    >(`/router/resolve/${encodeURIComponent(companyRoute)}/${encodeURIComponent(shareCode)}`, {
      params: passthrough,
    });
    return response.data.data;
  }

  // User & Company
  async getMe(companyId?: string): Promise<{
    user: {
      id: string;
      email?: string;
      username?: string;
      profilePicUrl?: string;
    };
    company: {
      id: string;
      onboarding?: {
        completed: boolean;
        currentStep?: number;
        completedAt?: string;
        skipped?: boolean;
      };
      termsAcceptedAt?: string;
      termsVersion?: string;
      route?: string; // whop.com/<route>; missing when Whop hasn't given one
    } | null;
  }> {
    const response = await this.client.get<
      ApiResponse<{
        user: {
          id: string;
          email?: string;
          username?: string;
          profilePicUrl?: string;
        };
        company: {
          id: string;
          onboarding?: {
            completed: boolean;
            currentStep?: number;
            completedAt?: string;
            skipped?: boolean;
          };
          termsAcceptedAt?: string;
          termsVersion?: string;
          route?: string;
        } | null;
      }>
    >('/me', {
      params: companyId ? { companyId } : undefined,
    });
    return response.data.data;
  }

  // Terms acceptance
  async acceptTerms(
    companyId: string,
    version: string
  ): Promise<{ termsAcceptedAt: string; termsVersion: string }> {
    const response = await this.client.post<
      ApiResponse<{ termsAcceptedAt: string; termsVersion: string }>
    >(`/terms/${companyId}/accept`, { version });
    return response.data.data;
  }

  // Onboarding
  async updateOnboardingStatus(
    companyId: string,
    data: {
      currentStep?: number;
      completed?: boolean;
      skipped?: boolean;
    }
  ): Promise<{
    completed: boolean;
    currentStep?: number;
    completedAt?: string;
    skipped?: boolean;
  }> {
    const response = await this.client.post<
      ApiResponse<{
        completed: boolean;
        currentStep?: number;
        completedAt?: string;
        skipped?: boolean;
      }>
    >(`/onboarding/${companyId}`, data);
    return response.data.data;
  }

  // Review Prompt
  async getReviewPromptStatus(companyId: string): Promise<{
    lastShownAt?: string;
    lastDismissedAt?: string;
    dismissCount: number;
    hasClickedReview: boolean;
    shouldShow: boolean;
  }> {
    const response = await this.client.get<
      ApiResponse<{
        lastShownAt?: string;
        lastDismissedAt?: string;
        dismissCount: number;
        hasClickedReview: boolean;
        shouldShow: boolean;
      }>
    >(`/review-prompt/${companyId}`);
    return response.data.data;
  }

  async recordReviewPromptShown(companyId: string): Promise<void> {
    await this.client.post(`/review-prompt/${companyId}/shown`);
  }

  async recordReviewPromptDismiss(companyId: string): Promise<void> {
    await this.client.post(`/review-prompt/${companyId}/dismiss`);
  }

  async recordReviewPromptClicked(companyId: string): Promise<void> {
    await this.client.post(`/review-prompt/${companyId}/clicked`);
  }

  // Products
  async listProducts(companyId: string): Promise<Product[]> {
    const response = await this.client.get<ApiResponse<Product[]>>('/products', {
      params: { companyId },
    });
    return response.data.data;
  }

  async listProductPlans(
    companyId: string,
    productId: string,
    visibility?: 'visible' | 'hidden' | 'archived'
  ): Promise<ProductPlan[]> {
    const response = await this.client.get<ApiResponse<ProductPlan[]>>(
      `/products/${productId}/plans`,
      {
        params: { companyId, visibility },
      }
    );
    return response.data.data;
  }

  // Billing
  async getBillingStatus(companyId: string): Promise<{
    plan: 'free' | 'pro';
    status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'free';
    hasProAccess: boolean;
    trialEndsAt?: string;
    currentPeriodEnd?: string;
  }> {
    const response = await this.client.get<
      ApiResponse<{
        plan: 'free' | 'pro';
        status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'free';
        hasProAccess: boolean;
        trialEndsAt?: string;
        currentPeriodEnd?: string;
      }>
    >('/billing/status', {
      params: { companyId },
    });
    return response.data.data;
  }

  // Inside your whop (member-facing experience view)
  // Storefront (public): who sold what, for buyer pages
  async getPurchaseInfo(experimentId: string): Promise<PurchaseInfo> {
    const response = await this.client.get<ApiResponse<PurchaseInfo>>(
      `/storefront/purchase/${encodeURIComponent(experimentId)}`
    );
    return response.data.data;
  }

  async getSeller(companyRoute: string): Promise<SellerInfo> {
    const response = await this.client.get<ApiResponse<SellerInfo>>(
      `/storefront/seller/${encodeURIComponent(companyRoute)}`
    );
    return response.data.data;
  }

  async getExperienceOffer(experienceId: string): Promise<ExperienceView> {
    const response = await this.client.get<ApiResponse<ExperienceView>>(
      `/experiences/${encodeURIComponent(experienceId)}/offer`
    );
    return response.data.data;
  }

  async createProCheckout(companyId: string): Promise<{
    planId: string;
    checkoutConfigurationId: string;
    purchaseUrl: string;
  }> {
    const response = await this.client.post<
      ApiResponse<{
        planId: string;
        checkoutConfigurationId: string;
        purchaseUrl: string;
      }>
    >('/billing/create-checkout', { companyId });
    return response.data.data;
  }

  async getBillingLimits(companyId: string): Promise<PlanLimits> {
    const response = await this.client.get<ApiResponse<PlanLimits>>('/billing/limits', {
      params: { companyId },
    });
    return response.data.data;
  }

  /** Direct browser → R2 upload via the presigned URL from createUnlockTest. */
  async uploadPaidPostContent(
    uploadUrl: string,
    file: File
  ): Promise<void> {
    try {
      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': file.type },
        timeout: 120000,
      });
    } catch (err) {
      console.error('Image upload failed:', err);
      throw new Error("Your image didn't finish uploading. Check your connection and try again.");
    }
  }

  // Hosted offer page — public endpoints (no auth)
  /** Legacy /p/ link: a migrated post's unlock test, else the post itself. */
  async resolveLegacyOffer(
    companyRoute: string,
    shareCode: string,
    visitorId?: string
  ): Promise<PublicOffer> {
    const response = await this.client.get<ApiResponse<PublicOffer>>(
      `/offers/p/${encodeURIComponent(companyRoute)}/${encodeURIComponent(shareCode)}`,
      { params: visitorId ? { vid: visitorId } : undefined }
    );
    return response.data.data;
  }

  async claimOfferPurchase(offer: PublicOffer, receiptId: string): Promise<{ token: string }> {
    if (offer.kind === 'post') return this.claimPaidPostPurchase(offer.offerId, receiptId);
    const response = await this.client.post<ApiResponse<{ token: string }>>(
      `/offers/${offer.offerId}/claim`,
      { receiptId }
    );
    return response.data.data;
  }

  async restoreOfferPurchase(offer: PublicOffer): Promise<{ token: string }> {
    if (offer.kind === 'post') return this.restorePaidPostPurchase(offer.offerId);
    const response = await this.client.post<ApiResponse<{ token: string }>>(
      `/offers/${offer.offerId}/restore`
    );
    return response.data.data;
  }

  async getOfferContentUrl(offer: PublicOffer, token: string): Promise<string> {
    if (offer.kind === 'post') return this.getPaidPostContentUrl(offer.offerId, token);
    const response = await this.client.get<ApiResponse<{ url: string }>>(
      `/offers/${offer.offerId}/content`,
      { params: { token } }
    );
    return response.data.data.url;
  }

  // Unlock tests — creator endpoints
  async createUnlockTest(
    data: CreateUnlockTestRequest
  ): Promise<{ experiment: Experiment; uploadUrl: string }> {
    const response = await this.client.post<
      ApiResponse<{ experiment: Experiment; uploadUrl: string }>
    >('/offers/unlock', data);
    return response.data.data;
  }

  /** A new upload URL for a draft whose image didn't finish uploading. */
  async renewUnlockUpload(experimentId: string, contentType: string, contentLength: number): Promise<string> {
    const response = await this.client.post<ApiResponse<{ uploadUrl: string }>>(
      `/offers/unlock/${experimentId}/upload-url`,
      { contentType, contentLength }
    );
    return response.data.data.uploadUrl;
  }

  async finalizeUnlockTest(experimentId: string): Promise<Experiment> {
    const response = await this.client.post<ApiResponse<Experiment>>(
      `/offers/unlock/${experimentId}/finalize`
    );
    return response.data.data;
  }

  async claimPaidPostPurchase(
    postId: string,
    receiptId: string
  ): Promise<{ token: string }> {
    const response = await this.client.post<ApiResponse<{ token: string }>>(
      `/posts/${postId}/claim`,
      { receiptId }
    );
    return response.data.data;
  }

  async restorePaidPostPurchase(postId: string): Promise<{ token: string }> {
    const response = await this.client.post<ApiResponse<{ token: string }>>(
      `/posts/${postId}/restore`
    );
    return response.data.data;
  }

  async getPaidPostContentUrl(postId: string, token: string): Promise<string> {
    const response = await this.client.get<ApiResponse<{ url: string }>>(
      `/posts/${postId}/content`,
      { params: { token } }
    );
    return response.data.data.url;
  }

  // Dev endpoints (only available in non-production)
  async getDevExperiments(): Promise<(Experiment & { stats: { visits: number; payments: number; conversions: number } })[]> {
    const response = await this.client.get<ApiResponse<(Experiment & { stats: { visits: number; payments: number; conversions: number } })[]>>('/dev/experiments');
    return response.data.data;
  }
}

export const api = new ApiService();
